import { X, Download, Pin, Bookmark } from "lucide-react";
import EtfSectorOverlay from "./EtfSectorOverlay";

const fmtPct = (n) => {
  if (n == null || isNaN(n)) return "—";
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

const INTERVAL_LABELS = {
  ipo: "Since IPO",
  since_2020: "2020 — Now",
  fy2025: "FY 2025",
  ytd: "YTD",
};

function compareCSV(rows, intervals, mode) {
  const hdr = ["Symbol", "Name", ...intervals.map((i) => `${INTERVAL_LABELS[i]} TR (${mode}) %`)];
  const csvRows = rows.map((row) => {
    const cells = [row.symbol, row.name];
    intervals.forEach((iid) => {
      const r = (row.results || []).find((x) => x.interval_id === iid);
      const val = r ? (mode === "reinvest" ? r.total_return_reinvest_pct : r.total_return_pct) : "";
      cells.push(val);
    });
    return cells;
  });
  const csv = [hdr, ...csvRows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `compare_${mode}_${rows.map((r) => r.symbol).join("-")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function CompareView({ rows, intervals, mode, onRemove, onClearAll, onSaveAsWatchlist }) {
  if (!rows || rows.length === 0) return null;

  // Determine winner per interval (highest TR)
  const winners = {};
  intervals.forEach((iid) => {
    let best = -Infinity;
    let bestSym = null;
    rows.forEach((row) => {
      const r = (row.results || []).find((x) => x.interval_id === iid);
      if (!r) return;
      const v = mode === "reinvest" ? r.total_return_reinvest_pct : r.total_return_pct;
      if (v != null && v > best) {
        best = v;
        bestSym = row.symbol;
      }
    });
    winners[iid] = bestSym;
  });

  return (
    <section
      data-testid="compare-view"
      className="mb-12 border border-zinc-800 bg-[#121214] animate-fade-in-up"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-900 flex-wrap gap-3">
        <div>
          <div className="text-label">Comparison</div>
          <div className="text-base font-bold tracking-tight mt-0.5">
            {rows.length} ticker{rows.length === 1 ? "" : "s"} pinned · {mode === "reinvest" ? "Reinvested" : "Simple"} TR
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onSaveAsWatchlist && (
            <button
              data-testid="save-compare-as-watchlist-btn"
              onClick={onSaveAsWatchlist}
              className="inline-flex items-center gap-2 border border-[#FFD60A]/40 text-[#FFD60A] hover:bg-[#FFD60A]/10 px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
            >
              <Bookmark size={12} /> Save as Watchlist
            </button>
          )}
          <button
            data-testid="compare-csv-btn"
            onClick={() => compareCSV(rows, intervals, mode)}
            className="inline-flex items-center gap-2 border border-zinc-800 hover:bg-zinc-900 text-zinc-300 hover:text-white px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
          >
            <Download size={12} /> CSV
          </button>
          <button
            data-testid="compare-clear-all-btn"
            onClick={onClearAll}
            className="inline-flex items-center gap-2 border border-zinc-800 hover:bg-zinc-900 text-zinc-300 hover:text-white px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
          >
            <X size={12} /> Clear All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-900 bg-[#0e0e10]">
              <th className="text-label text-left px-5 py-3 font-normal">Ticker</th>
              {intervals.map((iid) => (
                <th key={iid} className="text-label text-right px-5 py-3 font-normal">
                  {INTERVAL_LABELS[iid]}
                </th>
              ))}
              <th className="px-3 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.symbol} className="border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/30">
                <td className="px-5 py-4">
                  <div className="font-mono font-bold">{row.symbol}</div>
                  <div className="text-[11px] text-zinc-500 truncate max-w-[260px]">
                    {row.name}
                  </div>
                </td>
                {intervals.map((iid) => {
                  const r = (row.results || []).find((x) => x.interval_id === iid);
                  if (!r) return <td key={iid} className="text-right px-5 py-4 text-zinc-600 font-mono">—</td>;
                  const v = mode === "reinvest" ? r.total_return_reinvest_pct : r.total_return_pct;
                  const isWin = winners[iid] === row.symbol;
                  const up = v >= 0;
                  return (
                    <td
                      key={iid}
                      className={`text-right px-5 py-4 font-mono ${up ? "text-[#00E676]" : "text-[#FF3B30]"}`}
                      data-testid={`compare-cell-${row.symbol}-${iid}`}
                    >
                      <div className="flex items-center justify-end gap-2">
                        {isWin && rows.length > 1 && (
                          <span className="text-[9px] uppercase tracking-widest bg-[#FFD60A] text-black px-1.5 py-0.5">
                            Top
                          </span>
                        )}
                        {fmtPct(v)}
                      </div>
                    </td>
                  );
                })}
                <td className="px-3 py-4">
                  <button
                    data-testid={`compare-remove-${row.symbol}`}
                    onClick={() => onRemove(row.symbol)}
                    className="text-zinc-500 hover:text-white transition-colors p-1"
                    aria-label="Remove from compare"
                  >
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 pb-5 pt-2">
        <EtfSectorOverlay symbols={rows.map((r) => r.symbol)} />
      </div>
    </section>
  );
}
